import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toTitleCase } from '@benzinga/utils';
import { sanitizeHttpUrl } from '@benzinga/calendars';
import type { CompanyEventDetails } from '@benzinga/calendar-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import { formatTickerExchange } from '../../../../utils/exchange';
import { formatDate } from './utils';
import FAQs from '../../../components/FAQs';

interface EventsFAQProps {
  companyEvents: CompanyEventDetails[];
  quote: DelayedQuote;
}

export const EventsFAQ: React.FC<EventsFAQProps> = ({ companyEvents, quote }) => {
  const { t, i18n } = useTranslation('quote');

  const FAQsDataSet = [
    {
      answer: getUpcomingEventsAnswer(companyEvents, quote, t, i18n.language),
      question: t('Quote.FAQ.Questions.Events.what-upcoming-events', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getUpcomingEventsCountAnswer(companyEvents, quote, t),
      question: t('Quote.FAQ.Questions.Events.how-many-upcoming-events', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getEventTypesAnswer(companyEvents, quote, t),
      question: t('Quote.FAQ.Questions.Events.what-types-of-events', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getNextEventLocationAnswer(companyEvents, quote, t, i18n.language),
      question: t('Quote.FAQ.Questions.Events.where-is-next-event', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getJoinNextEventAnswer(companyEvents, quote, t, i18n.language),
      question: t('Quote.FAQ.Questions.Events.how-to-join-next-event', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getOfficialEventDetailsAnswer(companyEvents, quote, t, i18n.language),
      question: t('Quote.FAQ.Questions.Events.where-official-details', {
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
    {
      answer: getLastEventAnswer(companyEvents, quote, t, i18n.language),
      question: t('Quote.FAQ.Questions.Events.when-was-last-event', {
        exchange: formatTickerExchange(quote.bzExchange, quote.otcTier),
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      }),
    },
  ];

  return <FAQs FAQsDataSet={FAQsDataSet} title={quote.name} />;
};

export default EventsFAQ;

const formatEventType = (eventType: string): string => {
  return toTitleCase(eventType.trim().toLowerCase());
};

const formatStartTime = (raw: string | undefined): string | null => {
  if (!raw?.trim()) return null;
  const value = raw.trim();
  const fromHms = DateTime.fromFormat(value, 'HH:mm:ss');
  if (fromHms.isValid) return fromHms.toFormat('h:mm a');
  const fromIso = DateTime.fromISO(value);
  if (fromIso.isValid) return fromIso.toFormat('h:mm a');
  return value;
};

const getEventStartDay = (event: CompanyEventDetails): DateTime =>
  DateTime.fromISO(event.date_start).startOf('day');

const isUpcomingEvent = (event: CompanyEventDetails): boolean =>
  getEventStartDay(event) >= DateTime.now().startOf('day');

const isPastEvent = (event: CompanyEventDetails): boolean =>
  getEventStartDay(event) < DateTime.now().startOf('day');

const getUpcomingEvents = (events: CompanyEventDetails[]): CompanyEventDetails[] => {
  return events
    .filter(isUpcomingEvent)
    .sort((a, b) => DateTime.fromISO(a.date_start).toMillis() - DateTime.fromISO(b.date_start).toMillis());
};

const getPastEvents = (events: CompanyEventDetails[]): CompanyEventDetails[] => {
  return events
    .filter(isPastEvent)
    .sort((a, b) => DateTime.fromISO(b.date_start).toMillis() - DateTime.fromISO(a.date_start).toMillis());
};

const getUniqueEventTypes = (events: CompanyEventDetails[]): string[] => {
  return [...new Set(events.map(event => event.event_type?.trim()).filter(Boolean))].map(type =>
    formatEventType(type as string),
  );
};

const formatEventDate = (event: CompanyEventDetails, language: string): string => {
  const date = formatDate(DateTime.fromISO(event.date_start).setLocale(language));
  const time = formatStartTime(event.start_time);
  return time ? `${date} at ${time}` : date;
};

const getUpcomingEventsAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const upcomingEvents = getUpcomingEvents(events);

  if (upcomingEvents.length === 0) {
    return <p>{t('Quote.FAQ.Answers.Events.no-next-corporate-event', { quoteName: quote.name })}</p>;
  }

  const nextEvent = upcomingEvents[0];

  return (
    <p>
      {t('Quote.FAQ.Answers.Events.next-upcoming-event', {
        date: formatEventDate(nextEvent, language),
        eventName: nextEvent.event_name,
        eventType: formatEventType(nextEvent.event_type),
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      })}
    </p>
  );
};

const getUpcomingEventsCountAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
): React.ReactElement => {
  const upcomingCount = getUpcomingEvents(events).length;

  if (upcomingCount === 0) {
    return <p>{t('Quote.FAQ.Answers.Events.no-upcoming-events', { quoteName: quote.name })}</p>;
  }

  return (
    <p>
      {t(
        upcomingCount === 1
          ? 'Quote.FAQ.Answers.Events.upcoming-events-count-one'
          : 'Quote.FAQ.Answers.Events.upcoming-events-count-many',
        {
          count: upcomingCount,
          quoteName: quote.name,
          quoteSymbol: quote.symbol,
        },
      )}
    </p>
  );
};

const getEventTypesAnswer = (events: CompanyEventDetails[], quote: DelayedQuote, t: TFunction): React.ReactElement => {
  const eventTypes = getUniqueEventTypes(events);

  if (eventTypes.length === 0) {
    return <p>{t('Quote.FAQ.Answers.Events.no-event-types', { quoteName: quote.name })}</p>;
  }

  return (
    <p>
      {t('Quote.FAQ.Answers.Events.event-types', {
        eventTypes: eventTypes.join(', '),
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      })}
    </p>
  );
};

const getNextEventLocationAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const nextEvent = getUpcomingEvents(events)[0];
  const location = nextEvent?.location?.trim();

  if (!location) {
    return <p>{t('Quote.FAQ.Answers.Events.no-next-event-location', { quoteName: quote.name })}</p>;
  }

  return (
    <p>
      {t('Quote.FAQ.Answers.Events.next-event-location', {
        date: formatEventDate(nextEvent, language),
        eventName: nextEvent.event_name,
        location,
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      })}
    </p>
  );
};

const getJoinNextEventAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const nextEventWithJoinInfo = getUpcomingEvents(events).find(
    event => sanitizeHttpUrl(event.webcast_link) || event.phone_number?.trim(),
  );

  if (!nextEventWithJoinInfo) {
    return <p>{t('Quote.FAQ.Answers.Events.no-join-info', { quoteName: quote.name })}</p>;
  }

  const webcastUrl = sanitizeHttpUrl(nextEventWithJoinInfo.webcast_link);
  const phoneNumber = nextEventWithJoinInfo.phone_number?.trim();

  if (!webcastUrl && !phoneNumber) {
    return <p>{t('Quote.FAQ.Answers.Events.no-join-info', { quoteName: quote.name })}</p>;
  }

  const joinAnswerParams = {
    date: formatEventDate(nextEventWithJoinInfo, language),
    eventName: nextEventWithJoinInfo.event_name,
    quoteName: quote.name,
    quoteSymbol: quote.symbol,
  };
  const linkClassName = 'text-blue-600 hover:text-blue-800';

  return (
    <p>
      {webcastUrl ? (
        <>
          {t('Quote.FAQ.Answers.Events.join-event-webcast', joinAnswerParams)}{' '}
          <a className={linkClassName} href={webcastUrl} rel="noopener noreferrer" target="_blank">
            {t('Quote.FAQ.Answers.Events.join-event-webcast-link')}
          </a>
          {phoneNumber ? (
            <>
              {t('Quote.FAQ.Answers.Events.join-event-webcast-with-phone')}{' '}
              <a className={linkClassName} href={`tel:${phoneNumber.replace(/[^\d+]/g, '')}`}>
                {phoneNumber}
              </a>
              {t('Quote.FAQ.Answers.Events.join-event-end')}
            </>
          ) : (
            t('Quote.FAQ.Answers.Events.join-event-webcast-end')
          )}
        </>
      ) : (
        <>
          {t('Quote.FAQ.Answers.Events.join-event-phone', joinAnswerParams)}{' '}
          <a className={linkClassName} href={`tel:${phoneNumber!.replace(/[^\d+]/g, '')}`}>
            {phoneNumber}
          </a>
          {t('Quote.FAQ.Answers.Events.join-event-end')}
        </>
      )}
    </p>
  );
};

const getOfficialEventDetailsAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const nextEventWithSource = getUpcomingEvents(events).find(event => sanitizeHttpUrl(event.source_link));
  const sourceUrl = sanitizeHttpUrl(nextEventWithSource?.source_link);

  if (!nextEventWithSource || !sourceUrl) {
    return (
      <p>
        {t('Quote.FAQ.Answers.Events.no-official-details', { quoteName: quote.name })}{' '}
        <a className="text-blue-600 hover:text-blue-800" href="/calendars/events">
          {t('Quote.Events.footer-browse-market-link')}
        </a>
        .
      </p>
    );
  }

  return (
    <p>
      {t('Quote.FAQ.Answers.Events.official-details', {
        date: formatEventDate(nextEventWithSource, language),
        eventName: nextEventWithSource.event_name,
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      })}{' '}
      <a className="text-blue-600 hover:text-blue-800" href={sourceUrl} rel="noopener noreferrer" target="_blank">
        {t('Quote.FAQ.Answers.Events.view-official-source')}
      </a>
      .
    </p>
  );
};

const getLastEventAnswer = (
  events: CompanyEventDetails[],
  quote: DelayedQuote,
  t: TFunction,
  language: string,
): React.ReactElement => {
  const lastEvent = getPastEvents(events)[0];

  if (!lastEvent) {
    return <p>{t('Quote.FAQ.Answers.Events.no-past-events', { quoteName: quote.name })}</p>;
  }

  return (
    <p>
      {t('Quote.FAQ.Answers.Events.last-event', {
        date: formatEventDate(lastEvent, language),
        eventName: lastEvent.event_name,
        eventType: formatEventType(lastEvent.event_type),
        quoteName: quote.name,
        quoteSymbol: quote.symbol,
      })}
    </p>
  );
};
